import { Texture } from "pixi.js"
import type { ComponentType } from "react"
import { createRoot } from "react-dom/client"
import {
  ICON_COLORS,
  ICON_RENDER_TIMEOUT,
  ICON_RESOLUTION_MULTIPLIER,
  INACTIVE_ICON_COLORS
} from "@/domains/visualization/constants/graph"
import type { IconColors } from "@/domains/visualization/types/Graph"
import { applySvgStyling, svgToCanvas } from "./iconToTexture.utils"

interface IconToTextureOptions {
  size?: number
  colors?: IconColors
  isActive?: boolean
}

/**
 * Converts a React SVG icon component from @relex-solutions/fragments
 * into a Pixi RenderTexture that can be used in sprites
 */
export async function iconToTexture(
  IconComponent: ComponentType<{
    className?: string
    style?: React.CSSProperties
    colors?: IconColors
  }>,
  options: IconToTextureOptions = {}
): Promise<Texture> {
  const { size = 16, colors, isActive = true } = options

  // Determine final colors based on active state
  const finalColor = isActive ? (colors?.color ?? ICON_COLORS.PRIMARY) : INACTIVE_ICON_COLORS.color
  const finalPrimaryColor = isActive ? (colors?.primaryColor ?? ICON_COLORS.PRIMARY) : INACTIVE_ICON_COLORS.primaryColor
  const finalSecondaryColor = isActive
    ? (colors?.secondaryColor ?? ICON_COLORS.SECONDARY)
    : INACTIVE_ICON_COLORS.secondaryColor

  // Create a temporary container for rendering the React component
  const tempContainer = document.createElement("div")
  Object.assign(tempContainer.style, {
    position: "absolute",
    left: "-9999px",
    top: "-9999px",
    width: `${size.toString()}px`,
    height: `${size.toString()}px`
  })

  document.body.appendChild(tempContainer)

  try {
    // Render the React icon component
    const root = createRoot(tempContainer)

    await new Promise<void>((resolve) => {
      // Universal icon props that work for all @relex-solutions/fragments icon types
      const iconProps = {
        style: {
          width: size,
          height: size,
          color: finalColor,
          "--fr-icon-primary-color": finalPrimaryColor,
          "--fr-icon-secondary-color": finalSecondaryColor
        } as React.CSSProperties
      }

      root.render(
        <div
          style={{
            width: size,
            height: size,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <IconComponent {...iconProps} />
        </div>
      )

      setTimeout(resolve, ICON_RENDER_TIMEOUT)
    })

    // Find the SVG element
    const svgElement = tempContainer.querySelector("svg")

    if (!svgElement) {
      throw new Error("Icon component did not render an SVG element")
    }

    // Apply final styling based on actual rendered SVG classes
    applySvgStyling(svgElement, finalColor, finalPrimaryColor, finalSecondaryColor)

    // Create high-resolution canvas for quality
    const highResSize = size * ICON_RESOLUTION_MULTIPLIER
    const canvas = await svgToCanvas(svgElement, highResSize)

    // Create texture from canvas
    const texture = Texture.from(canvas)

    // Cleanup
    root.unmount()

    return texture
  } finally {
    // Always cleanup the temporary DOM element
    document.body.removeChild(tempContainer)
  }
}
